/**
 * TypeScript models mirroring the EPS Public referral create contract.
 *
 * Source of truth (EPS repo LD.Shared.EnterprisePlatformServices.API):
 *   EPS/Public/LD.EPS.Public.Api/Models/ThirdParty/Referral/*.cs
 *
 * The EPS Web API serializes these as camelCase JSON, so the property names
 * here match the JSON contract, not the C# PascalCase field names.
 */

export const LOAN_PURPOSES = ["purchase", "refinance"] as const;
export type LoanPurpose = (typeof LOAN_PURPOSES)[number];

export const PROPERTY_TYPES = [
  "SingleFamily",
  "Condo",
  "Townhouse",
  "MultiFamily",
  "ManufacturedHome",
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const REFINANCE_TYPES = ["RateTermRefinance", "CashOutRefinance"] as const;
export type RefinanceType = (typeof REFINANCE_TYPES)[number];

export const CREDIT_SCORE_RANGES = ["below 620", "620-679", "680-739", "740+"] as const;
export type CreditScoreRange = (typeof CREDIT_SCORE_RANGES)[number];

export const STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "VI", "GU", "AS", "MP",
] as const;
export type StateCode = (typeof STATE_CODES)[number];

export const EMPLOYMENT_STATUSES = [
  "Employed",
  "SelfEmployed",
  "Retired",
  "Military",
  "NotEmployed",
] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUSES)[number];

export const INCOME_RANGES = ["under50k", "50k-75k", "75k-100k", "100k-150k", "over150k"] as const;
export type IncomeRange = (typeof INCOME_RANGES)[number];

export const MILITARY_BRANCHES = [
  "Army",
  "Navy",
  "Air Force",
  "Marine Corps",
  "Coast Guard",
  "Space Force",
] as const;
export type MilitaryBranch = (typeof MILITARY_BRANCHES)[number];

export const MILITARY_SERVICE_STATUSES = ["Active", "Veteran", "Reserve", "Spouse"] as const;
export type MilitaryServiceStatus = (typeof MILITARY_SERVICE_STATUSES)[number];

/** ReferralBorrowerRequest */
export interface ReferralBorrowerRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  creditScoreRange?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

/** MilitaryServiceRequest */
export interface MilitaryServiceRequest {
  isMilitary: boolean;
  branch?: string;
  serviceStatus?: string;
}

/** EmploymentInfoRequest */
export interface EmploymentInfoRequest {
  employmentStatus?: string;
  incomeRange?: string;
}

/** CreatePurchaseReferralRequest */
export interface CreatePurchaseReferralRequest {
  partnerReferenceId?: string;
  borrower: ReferralBorrowerRequest;
  coBorrower?: ReferralBorrowerRequest;
  militaryService?: MilitaryServiceRequest;
  employment?: EmploymentInfoRequest;
  propertyState: string;
  propertyCity?: string;
  propertyZipCode?: string;
  propertyStreetAddress?: string;
  propertyType: string;
  estimatedValue: number;
  loanAmount: number;
}

/** CreateRefinanceReferralRequest */
export interface CreateRefinanceReferralRequest {
  partnerReferenceId?: string;
  borrower: ReferralBorrowerRequest;
  coBorrower?: ReferralBorrowerRequest;
  militaryService?: MilitaryServiceRequest;
  employment?: EmploymentInfoRequest;
  propertyState: string;
  propertyCity?: string;
  propertyZipCode?: string;
  propertyStreetAddress?: string;
  propertyType: string;
  currentLoanBalance: number;
  estimatedValue: number;
  loanAmount: number;
  refinanceType: string;
}

export type CreateReferralRequest =
  | CreatePurchaseReferralRequest
  | CreateRefinanceReferralRequest;

/** ReferralLinks */
export interface ReferralLinks {
  status?: string;
  detail?: string;
}

/** CreateReferralResponse */
export interface CreateReferralResponse {
  referralId: string;
  partnerReferenceId?: string;
  status: string;
  submittedAt: string;
  links?: ReferralLinks;
}

/**
 * Flat borrower form state. All values are raw strings straight from the DOM
 * inputs; the payload builder is responsible for trimming and shaping them.
 */
export interface BorrowerFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  creditScoreRange: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

/** Complete referral form state as read from the DOM. */
export interface ReferralFormState {
  loanPurpose: LoanPurpose | "";
  partnerReferenceId: string;
  borrower: BorrowerFormState;
  includeCoBorrower: boolean;
  coBorrower: BorrowerFormState;
  isMilitary: boolean;
  militaryBranch: string;
  militaryServiceStatus: string;
  employmentStatus: string;
  incomeRange: string;
  propertyStreetAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyZipCode: string;
  propertyType: string;
  estimatedValue: string;
  loanAmount: string;
  currentLoanBalance: string;
  refinanceType: string;
}

export function emptyBorrowerState(): BorrowerFormState {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    creditScoreRange: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
  };
}

export function emptyFormState(): ReferralFormState {
  return {
    loanPurpose: "purchase",
    partnerReferenceId: "",
    borrower: emptyBorrowerState(),
    includeCoBorrower: false,
    coBorrower: emptyBorrowerState(),
    isMilitary: true,
    militaryBranch: "",
    militaryServiceStatus: "",
    employmentStatus: "",
    incomeRange: "",
    propertyStreetAddress: "",
    propertyCity: "",
    propertyState: "",
    propertyZipCode: "",
    propertyType: "",
    estimatedValue: "",
    loanAmount: "",
    currentLoanBalance: "",
    refinanceType: "",
  };
}
